import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { MORE_MENU_GROUPS, isNavItemActive } from "@/lib/nav";
import { NavBadge } from "@/navigation/NavBadge";
import { NavIcon } from "@/navigation/NavIcon";
import { useNav } from "@/navigation/navContext";
import { useNavGroupAccess } from "@/navigation/useNavAccess";
import { useLocation } from "react-router-dom";

export function MoreMenuSheet() {
  const { pathname } = useLocation();
  const { moreMenuOpen, setMoreMenuOpen, badges } = useNav();
  const groups = useNavGroupAccess(MORE_MENU_GROUPS);

  const operationsItems = [
    {
      key: "electricity",
      label: "Strom",
      href: "/strom",
      icon: "Zap" as const,
    },
    {
      key: "site-plan",
      label: "Platzplan",
      href: "/platzplan",
      icon: "Map" as const,
    },
    {
      key: "planner",
      label: "Planer",
      href: "/planner",
      icon: "CalendarDays" as const,
    },
    {
      key: "guests",
      label: "Gäste",
      href: "/guests",
      icon: "Users" as const,
    },
    {
      key: "mail",
      label: "E-Mail",
      href: "/mail",
      icon: "Mail" as const,
    },
    {
      key: "contracts",
      label: "Verträge",
      href: "/contracts",
      icon: "ClipboardList" as const,
    },
    {
      key: "tickets",
      label: "Tickets",
      href: "/tickets",
      icon: "LifeBuoy" as const,
      badgeKey: "ticketsOpen" as const,
    },
    {
      key: "visitors",
      label: "Besucher",
      href: "/besucher",
      icon: "Users" as const,
    },
  ];

  return (
    <Transition show={moreMenuOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50 lg:hidden"
        onClose={() => setMoreMenuOpen(false)}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/60" aria-hidden />
        </TransitionChild>

        <div
          className="fixed inset-x-0 bottom-0 flex max-h-[min(85dvh,32rem)] flex-col overflow-hidden"
          style={{ paddingBottom: "var(--eh-content-bottom-inset)" }}
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-250"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <DialogPanel className="eh-modal-panel mx-auto flex min-h-0 w-full max-w-lg flex-1 flex-col overflow-hidden rounded-t-2xl border border-separator bg-card shadow-ios-lg">
              <div className="flex shrink-0 items-center justify-between border-b border-separator px-4 py-3">
                <DialogTitle className="text-base font-semibold text-label">Menü</DialogTitle>
                <button
                  type="button"
                  className="rounded-lg px-3 py-1.5 text-sm font-medium text-accent"
                  onClick={() => setMoreMenuOpen(false)}
                >
                  Schließen
                </button>
              </div>
              <div className="eh-modal-panel__body px-2 py-2">
                <section className="mb-3">
                  <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                    Tagesgeschäft
                  </p>
                  <ul>
                    {operationsItems.map((item) => (
                      <li key={item.key}>
                        <NavLink
                          to={item.href}
                          onClick={() => setMoreMenuOpen(false)}
                          className={clsx(
                            "flex min-h-[3rem] items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                            isNavItemActive(pathname, item.href)
                              ? "bg-accent-soft text-accent"
                              : "text-label hover:bg-fill",
                          )}
                        >
                          <NavIcon name={item.icon} size={22} />
                          <span className="flex-1">{item.label}</span>
                          <NavBadge item={item} badges={badges} />
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </section>

                {groups.map((group) => (
                  <section key={group.key} className="mb-3">
                    <p className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-label-tertiary">
                      {group.label}
                    </p>
                    <ul>
                      {group.items.map((item) => (
                        <li key={item.key}>
                          <NavLink
                            to={item.href}
                            onClick={() => setMoreMenuOpen(false)}
                            className={clsx(
                              "flex min-h-[3rem] items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors",
                              isNavItemActive(pathname, item.href)
                                ? "bg-accent-soft text-accent"
                                : "text-label hover:bg-fill",
                            )}
                          >
                            {item.icon ? <NavIcon name={item.icon} size={22} /> : null}
                            <span className="flex-1">{item.label}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
